import { ArrowUpOutlined } from "@ant-design/icons";
import { Flex, Input, Spin } from "antd";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useChatbot } from "../contexts/ChatContext";
import InputOptionsPanel from "./InputOptionsPanel";
import MessageBlock from "./MessageBlock";

import styles from "./Chatbot.module.css";

const { TextArea } = Input;

const Chatbot = ({ emptyStateText = "What's on your mind about this?" }) => {
    const [input, setInput] = useState("");
    const [assistantMinHeight, setAssistantMinHeight] = useState(0);
    const [hasSelection, setHasSelection] = useState(false);

    const {
        messages,
        setMessages,
        fetchChatResponse,
        isStreaming,
        isLoadingBlock,
        scrollTrigger,
        activeInputBlock,
        dismissActiveInputBlock,
        sendStructuredResponse,
        readOnly,
        closedNoticeText,
        placeholder,
        failedTurn,
        clearFailedTurn,
        streamStalledMs,
    } = useChatbot();

    const textAreaRef = useRef(null);

    useEffect(() => {
        if (!failedTurn) return;
        if (failedTurn.userInput?.type === "message") {
            setInput(failedTurn.userInput.content ?? "");
            textAreaRef.current?.focus({ cursor: "end" });
        }
        // request_user_input_response case: rollback already restored the prior
        // assistant message, so the activeInputBlock effect in ChatContext
        // re-arms InputOptionsPanel on its own -- nothing to do here.
    }, [failedTurn]);

    const inputPlaceholder = activeInputBlock
        ? "Or type your own answer..."
        : (placeholder || "Reply...");

    const containerRef = useRef(null);              // The scrollable div
    const lastAssistantMessageRef = useRef(null);
    const lastUserMessageRef = useRef(null);
    const optionsPanelRef = useRef(null);
    const scrollIntentRef = useRef(null);           // "user" | "init" | null
    const hasInitializedRef = useRef(false);
    const prevTriggerRef = useRef(scrollTrigger);
    const isPinnedRef = useRef(false);

    const lastUserIndex = messages.map(m => m.role).lastIndexOf("user");
    const lastAssistantIndex = messages.map(m => m.role).lastIndexOf("assistant");
    const setLastAssistantRef = useCallback((node) => { lastAssistantMessageRef.current = node; }, []);
    const setLastUserRef = useCallback((node) => { lastUserMessageRef.current = node; }, []);

    const scrollAssistantToTop = useCallback(() => {
        const container = containerRef.current;
        const assistantTarget = lastAssistantMessageRef.current;
        if (!container || !assistantTarget) return;

        const containerStyle = window.getComputedStyle(container);
        const padTop = parseFloat(containerStyle.paddingTop) || 0;
        const padBottom = parseFloat(containerStyle.paddingBottom) || 0;
        const available = container.clientHeight - padTop - padBottom;

        const targetStyle = window.getComputedStyle(assistantTarget);
        const marginTop = parseFloat(targetStyle.marginTop) || 0;
        
        setAssistantMinHeight(available);
        isPinnedRef.current = true;

        // Wait one frame for the new min-height to take effect, then scroll.
        requestAnimationFrame(() => {
            const targetTop = assistantTarget.offsetTop - padTop + marginTop;
            container.scrollTop = targetTop;
        });
    }, []);

    useLayoutEffect(() => {
        const isInit = !hasInitializedRef.current && messages.length > 0;
        const isUserIntent = scrollIntentRef.current === "user";
        const isTriggered = scrollTrigger > prevTriggerRef.current;

        if (isInit || isUserIntent || isTriggered) {
            scrollAssistantToTop();

            if (isInit) hasInitializedRef.current = true;
            scrollIntentRef.current = null;
            prevTriggerRef.current = scrollTrigger;
        }
    }, [messages, scrollTrigger, scrollAssistantToTop]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new ResizeObserver(() => {
            if (!isPinnedRef.current) return;
            scrollAssistantToTop();
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [scrollAssistantToTop]);

    // Once the session closes, the bottom panel swaps to the closed notice,
    // which resizes the messages container -- without unpinning here, the
    // ResizeObserver above would treat that layout shift as a reason to
    // re-measure and re-scroll, producing a visible flash right at close.
    useEffect(() => {
        if (readOnly) isPinnedRef.current = false;
    }, [readOnly]);

    const handleStructuredResponse = (requestId, selectedOptionIds) => {
        scrollIntentRef.current = "user";
        sendStructuredResponse(requestId, selectedOptionIds);
    };

    const handleSend = () => {
        if (isStreaming || readOnly) return;

        clearFailedTurn();

        if (input.trim()) {
            scrollIntentRef.current = "user";
            dismissActiveInputBlock();

            const userInputObj = {
                type: "message",
                content: input
            };

            const newUserMessage = {
                role: "user",
                blocks: [{ block_type: "text", text: input }]
            };

            const assistantPlaceholder = {
                role: "assistant", blocks: []
            };

            setMessages(prev => [...prev, newUserMessage, assistantPlaceholder]);
            fetchChatResponse(userInputObj);
            setInput("");
            return;
        }

        if (hasSelection && optionsPanelRef.current?.confirm()) {
            return;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !isStreaming) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className={styles.chatWrapper}>
            <div ref={containerRef} className={styles.chatMessages}>
                {messages.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>{emptyStateText}</p>
                    </div>
                ) : (
                    <Flex vertical>
                        {messages.map((msg, index) => {
                            const isLastUser = index === lastUserIndex;
                            const isLastAssistant = index === lastAssistantIndex;

                            let ref = null;
                            if (isLastUser) ref = setLastUserRef;
                            else if (isLastAssistant) ref = setLastAssistantRef;

                            return (
                                <div
                                    key={index}
                                    ref={ref}
                                    className={`${styles.messageRow} ${msg.role === "user" ? styles.user : styles.assistant}`}
                                    style={isLastAssistant ? { minHeight: assistantMinHeight } : undefined}
                                >
                                    <div className={`${styles.bubble} ${msg.role === "user" ? styles.userBubble : styles.assistantBubble}`}>
                                        {msg.role === "assistant" && isStreaming && msg.blocks.length === 0 ? (
                                            <>
                                                <div className={styles.typingBubble} />
                                                {isLastAssistant && streamStalledMs >= 5000 && (
                                                    <div className={styles.stallCaption}>
                                                        {streamStalledMs >= 15000
                                                            ? "This is taking longer than usual."
                                                            : "Still thinking…"}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {(msg.blocks || []).map((block, bIdx) => (
                                                    <MessageBlock key={bIdx} block={block} />
                                                ))}
                                                {isLastAssistant && isLoadingBlock && (
                                                    <div className={styles.typingDots}>
                                                        <span /><span /><span />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </Flex>
                )}
                {failedTurn && (
                    <div className={styles.turnFailedNotice}>
                        I couldn't get through just now. Please try again later.
                    </div>
                )}
            </div>

            {readOnly ? (
                <div className={styles.closedNotice}>{closedNoticeText ?? "Session complete. Advance to the next step."}</div>
            ) : (
                <>
                    <InputOptionsPanel
                        ref={optionsPanelRef}
                        dimmed={input.trim().length > 0}
                        onSend={handleStructuredResponse}
                        onSelectionChange={setHasSelection}
                    />

                    <div className={styles.inputArea}>
                        <TextArea
                            ref={textAreaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={inputPlaceholder}
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            onPressEnter={handleKeyDown}
                            className={styles.textarea}
                            style={{ paddingTop: 7, paddingBottom: 7 }}
                        />
                        {isStreaming ? (
                            <Spin className={styles.spin} />
                        ) : (input.trim() || hasSelection) ? (
                            <button
                                className={styles.sendButton}
                                onClick={handleSend}
                            >
                                <ArrowUpOutlined />
                            </button>
                        ) : null}
                    </div>
                </>
            )}
        </div>
    );
};

export default Chatbot;